'use client';

import { useEffect, useRef } from 'react';

/**
 * Tom de telefone via Web Audio API (sem arquivo de audio). Dois modos:
 *  - 'incoming'  (padrao): 2 beeps curtos + pausa a cada 1.5s — chamada RECEBIDA.
 *  - 'ringback': tom longo de ~1s a cada 4s — o "puuu...." de chamada de SAIDA.
 *    Necessario porque o browser NAO gera ringback sozinho ao receber 180 Ringing
 *    (softphone/telefone IP geram nativamente; webphone precisa deste tom). E o
 *    ringback server-side (Dial `r`) nao chega no browser: a midia WebRTC (DTLS)
 *    so sobe no atendimento, entao nao ha caminho de audio na fase "chamando".
 *
 * @param playing quando true, toca. Quando false, para e libera o AudioContext.
 * @param mode qual cadencia tocar (default 'incoming', preserva chamadas existentes).
 */
export function useRingtone(playing: boolean, mode: 'incoming' | 'ringback' = 'incoming') {
  const ctxRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!playing) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (ctxRef.current) {
        ctxRef.current.close().catch(() => {});
        ctxRef.current = null;
      }
      return;
    }

    // Cria/reuse AudioContext
    if (!ctxRef.current) {
      try {
        const AudioCtx =
          (window as unknown as {
            AudioContext: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }).AudioContext ??
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext;
        ctxRef.current = new AudioCtx();
      } catch {
        return;
      }
    }

    const playBeep = (durationMs = 250) => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 480; // tom de telefone classico
      osc.type = 'sine';
      gain.gain.value = 0.05;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + durationMs / 1000);
    };

    // 'incoming': beep-beep-pausa a cada 1.5s. 'ringback': tom longo a cada 4s
    // (cadencia de chamada de saida no Brasil — ~1s tocando, ~4s de silencio).
    const playPattern =
      mode === 'ringback'
        ? () => playBeep(1000)
        : () => {
            playBeep(250);
            setTimeout(() => playBeep(250), 350);
          };
    const intervalMs = mode === 'ringback' ? 4000 : 1500;

    playPattern(); // imediato
    intervalRef.current = setInterval(playPattern, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Revisão 16/07: fechar o AudioContext TAMBÉM aqui. O ramo `if (!playing)` só
      // fecha na transição playing→false; no UNMOUNT com playing=true (o comum: o modal
      // de incoming desmonta no instante que a chamada é atendida/rejeitada) o cleanup
      // rodava sem fechar → vazava 1 contexto por chamada. Depois de ~6, o Chrome lança
      // em `new AudioContext()`, engolido pelo try/catch, e o ramal fica MUDO de ringtone
      // o resto da sessão.
      if (ctxRef.current) {
        ctxRef.current.close().catch(() => {});
        ctxRef.current = null;
      }
    };
  }, [playing, mode]);
}
